
import { Component, Input, ChangeDetectionStrategy, signal, ViewChild, ElementRef, AfterViewInit, OnChanges, OnDestroy, inject, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { IonicModule } from '@ionic/angular';


import { IsImageUrlPipe } from 'src/shared/pipes/is-image-url.pipe';
import { IsPdfUrlPipe } from 'src/shared/pipes/is-pdf-url.pipe';
import { IsCsvUrlPipe } from 'src/shared/pipes/is-csv-url.pipe';
import { FilenameFromUrlPipe } from 'src/shared/pipes/filename-from-url.pipe';
import { CsvPreviewComponent } from 'src/shared/components/csv-preview/csv-preview.component';

import { ImagePreviewComponent } from 'src/shared/components/image-preview/image-preview.component';


@Component({
  selector: 'attachment-carousel',
  standalone: true,
  imports: [
    CommonModule,

    IonicModule,

    IsImageUrlPipe,
    IsPdfUrlPipe,
    IsCsvUrlPipe,
    FilenameFromUrlPipe,
    CsvPreviewComponent,

    ImagePreviewComponent,

  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./attachment-carousel.component.scss'],
  template: `

    <div class="attachments-grid" #grid>
      @for (file of files; track file) {
        <div class="attachment-item">
          @if (file | isImageUrl) {
            <div class="image-attachment-wrapper">
              @if (!loadedImages().has(file)) {
                <ion-skeleton-text animated="true" class="image-skeleton" />
              }
              @if (visibleImages().has(file)) {
                <img
                  [src]="file"
                  class="attachment-image"
                  [class.loaded]="loadedImages().has(file)"
                  (load)="onImageLoaded(file)"
                  (click)="openImagePreview(file)" />
              }
              @if (downloadAction) {
                @if (twilioImages().has(file)) {
                  <button class="download-btn" (click)="onFileClick(file)">
                    <img src="assets/svg/open-outline.svg" class="download-icon" />
                  </button>
                } @else {
                  <button class="download-btn" (click)="downloadImage($event, file)">
                    <img src="assets/svg/download-outline.svg" class="download-icon" />
                  </button>
                }
              }
            </div>
          } @else if (file | isPdfUrl) {
            <div class="pdf-preview-wrapper">
              @if (pdfBlobUrls().get(file); as blobUrl) {
                <object [data]="blobUrl" type="application/pdf" class="pdf-object"></object>
              }
              <div class="pdf-card" (click)="onFileClick(file)">
                <div class="pdf-card-icon">
                  <img src="assets/svg/document-text-outline.svg" class="pdf-icon-img" />
                  <span class="pdf-badge">PDF</span>
                </div>
                <span class="pdf-card-name">{{ file | filenameFromUrl }}</span>

                @if (downloadAction) {
                  <button class="download-btn" (click)="downloadAction(file); $event.stopPropagation()">
                    <img src="assets/svg/download-outline.svg" class="download-icon" />
                  </button>
                }
              </div>

            </div>
          } @else if (file | isCsvUrl) {
            <csv-preview [src]="file" [downloadAction]="downloadAction" />
          } @else {
            <div class="file-attachment" (click)="onFileClick(file)">
              <img src="assets/svg/document-outline.svg" class="file-icon" />
              <span class="file-name">{{ file | filenameFromUrl }}</span>
              @if (downloadAction) {
                <button class="download-btn" (click)="downloadAction(file); $event.stopPropagation()">
                  <img src="assets/svg/download-outline.svg" class="download-icon" />
                </button>
              }
            </div>

          }
        </div>
      }
    </div>

    <image-preview
      [isOpen]="showImagePreview()"
      [imageSrc]="previewImageSrc()"
      (dismiss)="closeImagePreview()" />

  `
})
export class AttachmentCarouselComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() files: string[] = [];
  @Input() downloadAction?: (url: string) => void;


  @ViewChild('grid') gridRef: ElementRef<HTMLDivElement>;


  private zone = inject(NgZone);
  private sanitizer = inject(DomSanitizer);
  private isPdfPipe = new IsPdfUrlPipe();


  readonly pdfBlobUrls = signal(new Map<string, SafeResourceUrl>());
  readonly twilioImages = signal(new Set<string>());
  readonly visibleImages = signal(new Set<string>());
  readonly loadedImages = signal(new Set<string>());
  readonly showImagePreview = signal(false);
  readonly previewImageSrc = signal('');

  private fetchedPdfs = new Set<string>();
  private blobUrlsToRevoke: string[] = [];
  private intersectionObserver: IntersectionObserver | null = null;

  ngOnChanges(): void {
    this.fetchPdfs();
    const twilio = new Set<string>();
    for (const file of this.files) {
      if (file.includes('/Media/ME') || file.includes('twiliocdn.com')) {
        twilio.add(file);
      }
    }
    this.twilioImages.set(twilio);
  }

  ngAfterViewInit(): void {
    if (!this.gridRef) {return;}
    this.observeItemVisibility(this.gridRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();

    this.blobUrlsToRevoke.forEach(url => URL.revokeObjectURL(url));
  }

  onFileClick(url: string): void {
    window.open(url, '_blank');
  }


  onImageLoaded(file: string): void {
    const updated = new Set(this.loadedImages());
    updated.add(file);
    this.loadedImages.set(updated);
  }

  openImagePreview(src: string): void {
    this.previewImageSrc.set(src);
    this.showImagePreview.set(true);
  }

  closeImagePreview(): void {
    this.showImagePreview.set(false);
    this.previewImageSrc.set('');
  }

  async downloadImage(event: Event, file: string): Promise<void> {
    try {
      const response = await fetch(file);
      const contentType = (response.headers.get('content-type') || '').split(';')[0].trim();
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      const pathname = new URL(file).pathname;
      let filename = decodeURIComponent(pathname.split('/').pop() || 'image');
      if (!filename.includes('.')) {
        const ext = contentType.startsWith('image/')
          ? (contentType === 'image/jpeg' ? '.jpg' : `.${contentType.split('/')[1]}`)
          : '.jpg';
        filename += ext;
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      window.open(file, '_blank');
    }
  }

  private observeItemVisibility(grid: HTMLDivElement): void {
    const items = grid.querySelectorAll('.attachment-item');
    const itemToFile = new Map<Element, string>();
    items.forEach((item, i) => {
      if (i < this.files.length) {
        itemToFile.set(item, this.files[i]);
      }
    });
    this.intersectionObserver = new IntersectionObserver((entries) => {
      const updated = new Set(this.visibleImages());
      let changed = false;
      for (const entry of entries) {
        if (!entry.isIntersecting) {continue;}
        const file = itemToFile.get(entry.target);
        if (file && !updated.has(file)) {
          updated.add(file);
          changed = true;
        }
      }
      if (changed) {
        this.zone.run(() => this.visibleImages.set(updated));
      }
    }, { rootMargin: '200px 0px', threshold: 0 });
    items.forEach(item => this.intersectionObserver!.observe(item));

  }

  private fetchPdfs(): void {
    for (const url of this.files) {
      if (!this.isPdfPipe.transform(url)) {continue;}
      if (this.fetchedPdfs.has(url)) {continue;}
      this.fetchedPdfs.add(url);
      this.fetchPdfBlob(url);
    }
  }

  private async fetchPdfBlob(url: string): Promise<void> {
    try {
      const response = await fetch(url);
      if (!response.ok) {return;}
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      this.blobUrlsToRevoke.push(blobUrl);
      const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
      this.zone.run(() => {
        const updated = new Map(this.pdfBlobUrls());
        updated.set(url, safeUrl);
        this.pdfBlobUrls.set(updated);
      });
    } catch {
      // Silently fail - the PDF card is always shown as fallback
    }
  }


}
